import { useCallback, useEffect, useRef, useState } from "react";
import utilsService from "api/utils/utils.service";
import { ISvgConvertStatusResponse, isSvgConvertImmediateResult, isSvgConvertJobAccepted, unwrapUtilsBody } from "api/utils/utils.type";
import { FIVE_SECONDS_MS, TWENTY_SECONDS_MS, useSocketFallback } from "hooks/useSocketFallback";

/**
 * One tech-pack SVG conversion, whichever shape the backend answers in.
 *
 * **Why this hook exists.** The SVG engine moved to gpt-6-astra (BE branch
 * `astra-svg-default-pipeline`): median 81 s, worst measured 283 s across 24 production drawings, so
 * `utils/download_tech_pack` can no longer answer `file_type=svg` synchronously. It now replies
 * **202 + `{ job_id, status, status_url, credit_reserved }`** and the result is polled from
 * `utils/svg_convert_status/{job_id}`. The raster formats still answer **200 + `download_url`**
 * synchronously, so the hook resolves both shapes. (The BE's former `SVG_ASTRA` kill switch was
 * removed — the AI engine is the only SVG path.)
 *
 * Five call sites consume this endpoint for SVG and every one of them was written as
 * `request → read download_url → consume`. Rather than teach each of them the queue, they all await
 * this hook and switch on one outcome; their spinners, snackbars, analytics, filenames and
 * persistence stay exactly as they were.
 *
 * **`[LONG_JOB_SOCKET_FALLBACK]`** — this is a `{ job_id }` endpoint, so the polling belongs to
 * `useSocketFallback`, never to a hand-rolled `setTimeout` / `setInterval`. It fits because
 * `customStatusCheck` can drive any authenticated GET, and it supplies the canonical cadence
 * (20 s wait → 5 s poll → 20 min budget, REF-5923) that a hand-rolled loop always gets wrong. The
 * 20 min ceiling clears both the handover's 6 min floor and the 283 s worst case by a wide margin.
 *
 * `designTypes` is deliberately **empty**: Astra publishes no WebSocket frame for this job, so an
 * empty list makes `designTypes.includes(msg.design_type)` permanently false in both of the hook's
 * match paths and the job can only ever resolve by polling. That is the same poll-only situation
 * `usePollWhileGenerating` documents for Photoshoot Studio (REF-6185), and the same
 * `customStatusCheck` shape `TechPackProvider` already uses for the plain-GET pages-status endpoint.
 *
 * ⚠️ **The HTTP status is invisible here.** `HttpClient.get` ends with `return response.data`, so a
 * 202 arrives as a bare body with no `_handled` flag, and `res.status` is the *body's* status string
 * ("queued"), never 202. The queued-vs-immediate branch is therefore a response-shape guard.
 */

/**
 * The credit key the queued SVG conversion reserves under (BE `utils_handler.SVG_CREDIT_ACTION`; seeded into both
 * credit settings rows). Not `get_vector_file` — that is the old synchronous download's, absent from the multiplier.
 */
export const SVG_CONVERT_CREDIT_KEY = "svg_convert";

/** Terminal outcome of one conversion. Every caller must handle all four. */
export type TSvgConversionOutcome =
  /** `download_url` is ready — consume it exactly as the legacy synchronous body was consumed. */
  | { kind: "done"; downloadUrl: string; editorUrl?: string; jobId?: string }
  /** The interceptor already ran the UX (`_handled` 401/403/409/503, or a 402 paywall). Clear
   *  loading state and show NOTHING — a second snackbar would be a duplicate. */
  | { kind: "handled" }
  /** The job failed, or the response matched neither shape. `error` is the BE's user-displayable
   *  text when it supplied one; fall back to the call site's existing translated message. */
  | { kind: "failed"; error?: string }
  /** Monitoring expired. This does NOT mean the job failed, was cancelled, or was refunded — the
   *  worker may still be running. Never claim a refund in copy on this branch. */
  | { kind: "timeout" };

/** What the caller learns while it waits. */
interface ConvertHooks {
  /** The BE queued the job: its id, for a caller that keeps it somewhere a reload can find it (`resume`). */
  onJobAccepted?: (jobId: string) => void;
}

interface ConvertArgs {
  img_url: string;
  /** `svg` enters the queue; `png`/`jpg` still answer synchronously and resolve `done` immediately. */
  file_type: string;
}

/** Frozen at module scope so the identity is stable across renders — `useSocketFallback` keys
 *  several `useCallback`s on it. Empty by design; see the note above. */
const NO_SOCKET_TYPES: readonly string[] = Object.freeze([]);

/** A poll 404 is terminal: the job is missing or belongs to someone else (the BE does not
 *  distinguish them). Flagged through the payload because `useSocketFallback` swallows any throw
 *  from `customStatusCheck` and would otherwise keep polling to the 20-minute deadline. */
interface StatusPayload extends Partial<ISvgConvertStatusResponse> {
  __handled?: boolean;
  __missing?: boolean;
  /** The job id this poll was FOR, stamped by the adapter. `useSocketFallback` hands its callbacks the
   *  payload but not the id, so without this a callback cannot tell whose result it is holding. */
  __pollId?: string;
}

/**
 * One conversion attempt, from the request to a settled promise.
 *
 * The whole operation — not just the polling phase — is one object, because everything that can go
 * wrong here is a lifecycle race across the initiating `await`:
 * - `convert()` called twice in quick succession would fire two credit-reserving requests, and the
 *   second `startMonitoring` would silently drop the first job's monitor with its caller still awaiting.
 * - An unmount or `abandon()` *during* initiation would install a resolver afterwards and start a
 *   monitor that `useSocketFallback` then refuses to poll (its own mount flag is already false), so
 *   nothing would ever settle the promise.
 *
 * `active` is therefore claimed **synchronously**, before the first `await`, and `settled` makes
 * settlement idempotent so a late callback for a cancelled operation is a no-op.
 */
interface SvgConversionOp {
  settled: boolean;
  cancelled: boolean;
  /** The job this operation owns, once the BE has handed one over. `null` while still initiating.
   *  This is the session identity that keeps an abandoned job's in-flight poll from settling — or
   *  tearing down the monitor of — the conversion that replaced it. */
  jobId: string | null;
  resolve: (outcome: TSvgConversionOutcome) => void;
  reject: (error: unknown) => void;
}

export const useTechPackSvgConversion = () => {
  const [isConverting, setIsConverting] = useState(false);
  /** Credits the BE reserved for the in-flight job, straight from `credit_reserved`. A placeholder
   *  at 20 today and explicitly not final, so it is never written down in the FE. */
  const [creditReserved, setCreditReserved] = useState<number | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  /** The one in-flight operation, or null. Claimed synchronously by `convert()`. */
  const activeRef = useRef<SvgConversionOp | null>(null);
  const isMountedRef = useRef(true);

  /** Settle `op` exactly once and, if it is still the active operation, release the UI state. */
  const finalize = useCallback((op: SvgConversionOp, outcome: TSvgConversionOutcome) => {
    if (op.settled) return;
    op.settled = true;
    if (activeRef.current === op) activeRef.current = null;
    // Skip the state writes after unmount — React warns, and there is nothing left to render.
    if (isMountedRef.current) {
      setIsConverting(false);
      setJobId(null);
    }
    op.resolve(outcome);
  }, []);

  /** The operation a poll payload belongs to, or null when the answer is for a job nobody is waiting
   *  on any more. Belt-and-braces behind the adapter's staleness gate: a payload can only settle the
   *  operation that owns the job it was fetched for. */
  const ownerOf = useCallback((data: StatusPayload | undefined): SvgConversionOp | null => {
    const op = activeRef.current;
    if (!op || op.settled) return null;
    if (data?.__pollId && data.__pollId !== op.jobId) return null;

    return op;
  }, []);

  /** Give up on whatever is in flight. Idempotent; safe from an unmount cleanup. */
  const cancelActive = useCallback(
    (outcome: TSvgConversionOutcome) => {
      const op = activeRef.current;
      if (!op) return;
      op.cancelled = true;
      // Drop the job claim first: an in-flight poll for this job must find no owner even in the
      // window before `finalize` clears `activeRef`.
      op.jobId = null;
      finalize(op, outcome);
    },
    [finalize]
  );

  const { startMonitoring, stopMonitoring } = useSocketFallback({
    designTypes: NO_SOCKET_TYPES,
    isActive: true,
    waitDuration: TWENTY_SECONDS_MS,
    pollingInterval: FIVE_SECONDS_MS,
    // maxPollingDuration is left at the canonical 20 min default — well above the 283 s worst case
    // and the handover's "do not give up before 6 minutes".
    immediateCheck: false,
    customStatusCheck: async (id: string) => {
      // ── Session identity gate ────────────────────────────────────────────────────────────────
      //
      // `useSocketFallback.pollStatus` captures the monitored id when it starts, then `await`s this
      // adapter. If the user abandons job A and immediately starts job B, A's already-in-flight
      // status request lands *after* B took over — and `pollStatus` checks only whether the
      // component is mounted, not whose session the answer belongs to. It would then hand A's
      // payload to `onSuccess`/`onError` (settling B with A's result) and, worse, run its own
      // `cleanup()`, tearing down B's monitor while B has credits reserved.
      //
      // Returning `status: 0` for a stale poll is what prevents BOTH: 0 is the "still going" branch,
      // which invokes no callback and runs no cleanup. Checked twice — before the request, and again
      // after the await, because the active operation can change while it is in flight.
      const isStale = () => {
        const current = activeRef.current;

        return !current || current.settled || current.jobId !== id;
      };

      if (isStale()) return { status: 0 };

      let body: StatusPayload | undefined;

      try {
        const res = await utilsService.getSvgConvertStatus(id);
        if (isStale()) return { status: 0 };
        if ((res as { _handled?: boolean })?._handled) return { status: 99, data: { __handled: true, __pollId: id } };
        body = unwrapUtilsBody<StatusPayload>(res);
      } catch (error) {
        if (isStale()) return { status: 0 };
        // 404 → terminal (job missing or not ours). Anything else is transient: returning 0 keeps
        // the monitor alive inside its bounded budget instead of failing a healthy job on a blip.
        if ((error as any)?.response?.status === 404) return { status: 99, data: { __missing: true, __pollId: id } };

        return { status: 0 };
      }

      const data: StatusPayload = { ...body, __pollId: id };

      if (body?.status === "done") return { status: 1, type: "success", data };
      if (body?.status === "failed") return { status: 99, type: "failed", data };

      // `queued`, `processing`, and any value the BE adds later: still going.
      return { status: 0, data };
    },
    // Each of these belongs to whatever operation is active. `finalize` is idempotent, so a frame
    // arriving for an already-cancelled or already-settled operation does nothing.
    onSuccess: (data: StatusPayload) => {
      const op = ownerOf(data);
      if (!op) return;
      const downloadUrl = data?.download_url;
      if (!downloadUrl) {
        finalize(op, { kind: "failed" });

        return;
      }
      finalize(op, { kind: "done", downloadUrl, editorUrl: data?.editor_url, jobId: data?.job_id });
    },
    onError: (data: StatusPayload) => {
      const op = ownerOf(data);
      if (!op) return;
      if (data?.__handled) {
        finalize(op, { kind: "handled" });

        return;
      }
      finalize(op, { kind: "failed", error: data?.__missing ? undefined : data?.error || undefined });
    },
    // The deadline belongs to whatever session is being monitored now, and carries no payload to
    // check against — but a stale session cannot own the timer: `startRecursiveCall` clears the
    // previous one before installing its own.
    onTimeout: () => {
      const op = activeRef.current;
      if (op?.jobId) finalize(op, { kind: "timeout" });
    },
  });

  // `useSocketFallback` fires no callback once its own mount flag flips, so without this a caller
  // that unmounts mid-conversion — during initiation OR during polling — would await forever.
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      cancelActive({ kind: "handled" });
    };
  }, [cancelActive]);

  /**
   * Run one conversion end to end.
   *
   * Resolves for every queue outcome — only a non-402 failure of the *initiating* request rejects, so
   * a call site's existing `catch` keeps its original meaning (a genuine transport failure).
   *
   * The in-flight slot is claimed **synchronously**, before the first `await`: each conversion
   * reserves credits, so two clicks must not become two jobs, and a second `startMonitoring` would
   * drop the first monitor while its caller was still awaiting.
   */
  const convert = useCallback(
    (args: ConvertArgs, hooks: ConvertHooks = {}): Promise<TSvgConversionOutcome> => {
      if (activeRef.current) return Promise.resolve({ kind: "handled" });

      const op: SvgConversionOp = { settled: false, cancelled: false, jobId: null, resolve: () => undefined, reject: () => undefined };
      activeRef.current = op;
      setIsConverting(true);
      setCreditReserved(null);

      return new Promise<TSvgConversionOutcome>((resolve, reject) => {
        op.resolve = resolve;
        op.reject = reject;

        /** Settle by rejecting — used only for a real failure of the initiating request. */
        const fail = (error: unknown) => {
          if (op.settled) return;
          op.settled = true;
          if (activeRef.current === op) activeRef.current = null;
          if (isMountedRef.current) setIsConverting(false);
          reject(error);
        };

        utilsService
          .downloadTechPack(args)
          .then((res: any) => {
            // Unmounted or abandoned while the request was in flight: the promise is already
            // settled, so do NOT start a monitor that nothing would ever be able to resolve.
            if (op.cancelled || op.settled) return;

            // 401/403/409/503 resolve with `_handled` — the interceptor already ran the UX.
            if (res?._handled) {
              finalize(op, { kind: "handled" });

              return;
            }

            const body = unwrapUtilsBody<any>(res);

            // Immediate first: every synchronous non-SVG format. No polling.
            if (isSvgConvertImmediateResult(body)) {
              finalize(op, { kind: "done", downloadUrl: body.download_url, editorUrl: body.editor_url });

              return;
            }

            if (!isSvgConvertJobAccepted(body)) {
              finalize(op, { kind: "failed" });

              return;
            }

            setCreditReserved(typeof body.credit_reserved === "number" ? body.credit_reserved : null);
            op.jobId = body.job_id;
            setJobId(body.job_id);
            hooks.onJobAccepted?.(body.job_id);
            startMonitoring(body.job_id);
          })
          .catch((error: any) => {
            if (op.cancelled || op.settled) return;
            // 402's paywall snackbar is already dispatched by the interceptor
            // ([HTTP_HANDLED_RESPONSE]); re-reporting it would double up. Nothing was queued, so no
            // credit was reserved.
            if (error?.response?.status === 402) {
              finalize(op, { kind: "handled" });

              return;
            }
            fail(error);
          });
      });
    },
    [startMonitoring, finalize]
  );

  /**
   * Watch a job this browser did not start in this page — one queued before a reload (REF-7156: the id kept on the
   * canvas node). The same monitor, outcomes and single-slot rule as `convert`; nothing is requested, so nothing new
   * is reserved. A job the BE no longer has, or that is not this user's, polls 404 and settles `failed`.
   */
  const resume = useCallback(
    (existingJobId: string): Promise<TSvgConversionOutcome> => {
      if (activeRef.current) return Promise.resolve({ kind: "handled" });

      const op: SvgConversionOp = { settled: false, cancelled: false, jobId: existingJobId, resolve: () => undefined, reject: () => undefined };
      activeRef.current = op;
      setIsConverting(true);
      setJobId(existingJobId);

      return new Promise<TSvgConversionOutcome>((resolve, reject) => {
        op.resolve = resolve;
        op.reject = reject;
        startMonitoring(existingJobId);
      });
    },
    [startMonitoring]
  );

  /** Stop watching the job and settle the caller. The worker keeps running and the reservation is
   *  the BE's to resolve — this is not a cancellation of the job itself. */
  const abandon = useCallback(() => {
    stopMonitoring();
    cancelActive({ kind: "handled" });
  }, [stopMonitoring, cancelActive]);

  return { convert, resume, abandon, isConverting, creditReserved, jobId };
};

export default useTechPackSvgConversion;
